import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { OUTCOME_LABELS, getCurrentLocalDateTime } from "@/lib/utils";
import {
  Plus,
  Phone,
  Mail,
  Video,
  MessageSquare,
  FileText,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  CalendarPlus,
  ClipboardList,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { ContactInteraction, contactInteractionFormSchema } from "@shared/schema";

interface ContactHistoryTabProps {
  contactInteractions: ContactInteraction[];
  contactInteractionsLoading: boolean;
  clientId: string;
  clientName: string;
}

// Channel → pill colors + icon. Matches the handoff's "Type pills" spec:
// Email blue, Phone green, Meeting purple, Note (catch-all) amber.
const TYPE_PILL: Record<
  string,
  { label: string; bg: string; fg: string; Icon: React.ComponentType<{ className?: string }> }
> = {
  email:   { label: "Email",   bg: "#EFF6FF", fg: "#2563EB", Icon: Mail },
  call:    { label: "Phone",   bg: "#ECFDF5", fg: "#059669", Icon: Phone },
  meeting: { label: "Meeting", bg: "#F5F3FF", fg: "#7C3AED", Icon: Video },
  text:    { label: "Text",    bg: "#FEF3C7", fg: "#92400E", Icon: MessageSquare },
  other:   { label: "Note",    bg: "#FEF3C7", fg: "#92400E", Icon: FileText },
};

// Pre-formatter shared by Date column + Next follow-up cell.
const dateFmt = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});
function formatHandoffDate(value: unknown): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(d.getTime())) return "";
  return dateFmt.format(d);
}

// Compare against today in the viewer's timezone — don't bake in a fixed
// "today" so the OVERDUE chip stays accurate as time passes.
function isOverdue(value: unknown): boolean {
  if (!value) return false;
  const d = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

// Rows per page.
const PAGE_SIZE = 6;

type SortKey = "date" | "type" | "subject" | "outcome" | "followUp";

const SORT_COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: "date", label: "Date" },
  { key: "type", label: "Type" },
  { key: "subject", label: "Subject" },
  { key: "outcome", label: "Outcome" },
  { key: "followUp", label: "Next follow-up" },
];

const ContactHistoryTab = ({
  contactInteractions,
  contactInteractionsLoading,
  clientId,
  clientName,
}: ContactHistoryTabProps) => {
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Dates read most-recent-first by default; text columns A→Z.
      setSortDir(key === "date" || key === "followUp" ? "desc" : "asc");
    }
    setPage(1);
  };

  const contactForm = useForm({
    resolver: zodResolver(contactInteractionFormSchema),
    defaultValues: {
      clientId: parseInt(clientId, 10),
      type: "",
      subject: "",
      contactDate: getCurrentLocalDateTime(),
      notes: "",
      outcome: "",
      nextFollowUp: "",
    },
  });

  const createContactMutation = useMutation({
    mutationFn: async (data: any) => {
      const processedData = {
        ...data,
        contactDate: new Date(data.contactDate),
        nextFollowUp: data.nextFollowUp
          ? new Date(data.nextFollowUp + "T00:00:00")
          : undefined,
      };
      const response = await apiRequest(
        "POST",
        `/api/clients/${clientId}/contact-interactions`,
        processedData,
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/clients`, clientId, "contact-interactions"],
      });
      contactForm.reset();
      setIsContactDialogOpen(false);
      toast({ title: "Contact interaction added successfully", variant: "success" });
    },
    onError: (error) => {
      console.error("Contact interaction creation error:", error);
      toast({ title: "Failed to add contact interaction", variant: "destructive" });
    },
  });

  // Create a follow-up task from an interaction (due on the follow-up date).
  const addTaskMutation = useMutation({
    mutationFn: async (interaction: ContactInteraction) => {
      const requesterName =
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.email || "Broker";
      return apiRequest("POST", "/api/tasks", {
        title: `Follow up with ${clientName}: ${interaction.subject}`,
        description:
          `Follow-up from ${TYPE_PILL[interaction.type as string]?.label ?? "contact"} logged ` +
          `${formatHandoffDate(interaction.contactDate)}.` +
          (interaction.notes ? `\n\nNotes: ${interaction.notes}` : ""),
        priority: "medium",
        dueDate: interaction.nextFollowUp || undefined,
        category: "general",
        requesterName,
        requesterEmail: user?.email || "",
        requesterType: "broker",
        requesterId: user?.id,
        clientId: parseInt(clientId, 10),
        tags: [],
        source: "manual",
        createdBy: user?.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks/stats"] });
      toast({ title: "Task created", description: "Find it under Tasks.", variant: "success" });
    },
    onError: () => toast({ title: "Failed to create task", variant: "destructive" }),
  });

  // Create a 30-minute calendar appointment at 9:00 AM on the follow-up date.
  const addToCalendarMutation = useMutation({
    mutationFn: async (interaction: ContactInteraction) => {
      const start = new Date(interaction.nextFollowUp as any);
      start.setHours(9, 0, 0, 0);
      const end = new Date(start.getTime() + 30 * 60 * 1000);
      return apiRequest("POST", "/api/appointments", {
        title: `Follow up with ${clientName}`,
        description: interaction.subject || undefined,
        contactType: "client",
        contactId: parseInt(clientId, 10),
        type: interaction.type === "meeting" ? "meeting" : "call",
        startTime: start.toISOString(),
        endTime: end.toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/appointments"] });
      toast({
        title: "Added to calendar",
        description: "Scheduled for 9:00 AM on the follow-up date.",
        variant: "success",
      });
    },
    onError: () => toast({ title: "Failed to add to calendar", variant: "destructive" }),
  });

  // Filter → sort → paginate.
  const filteredInteractions = useMemo(() => {
    if (typeFilter === "all") return contactInteractions;
    return contactInteractions.filter((i) => (i.type || "other") === typeFilter);
  }, [contactInteractions, typeFilter]);

  const sortedInteractions = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const dateVal = (v: unknown) => {
      if (!v) return null;
      const t = new Date(v as any).getTime();
      return Number.isNaN(t) ? null : t;
    };
    return [...filteredInteractions].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "date":
          cmp = (dateVal(a.contactDate) ?? 0) - (dateVal(b.contactDate) ?? 0);
          break;
        case "type":
          cmp = (TYPE_PILL[a.type as string]?.label ?? "").localeCompare(
            TYPE_PILL[b.type as string]?.label ?? ""
          );
          break;
        case "subject":
          cmp = (a.subject || "").localeCompare(b.subject || "");
          break;
        case "outcome":
          cmp = (OUTCOME_LABELS[a.outcome as any] || "").localeCompare(
            OUTCOME_LABELS[b.outcome as any] || ""
          );
          break;
        case "followUp": {
          const av = dateVal(a.nextFollowUp);
          const bv = dateVal(b.nextFollowUp);
          // Rows without a follow-up always sink to the bottom.
          if (av === null && bv === null) cmp = 0;
          else if (av === null) return 1;
          else if (bv === null) return -1;
          else cmp = av - bv;
          break;
        }
      }
      if (cmp !== 0) return cmp * dir;
      return ((b.id ?? 0) - (a.id ?? 0));
    });
  }, [filteredInteractions, sortKey, sortDir]);

  const total = sortedInteractions.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleInteractions = sortedInteractions.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = (currentPage - 1) * PAGE_SIZE + visibleInteractions.length;

  const headerSubtitle =
    contactInteractions.length === 0
      ? "Log every touchpoint with this client to build a shared history."
      : `${contactInteractions.length} ${contactInteractions.length === 1 ? "touchpoint" : "touchpoints"} with this client`;

  // Reused twice (header right + empty-state CTA).
  const logContactTrigger = (props?: { variant?: "primary" | "ghost"; label?: string }) => (
    <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
      <DialogTrigger asChild>
        <PermissionButton
          permission="clients:edit"
          whenDenied="hide"
          style={
            props?.variant === "ghost"
              ? undefined
              : { backgroundColor: "#1D3FBD", color: "white" }
          }
          className={props?.variant === "ghost" ? undefined : "hover:opacity-90"}
        >
          <Plus className="h-4 w-4 mr-2" />
          {props?.label ?? "Log contact"}
        </PermissionButton>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Contact Interaction</DialogTitle>
        </DialogHeader>
        <Form {...contactForm}>
          <form
            onSubmit={contactForm.handleSubmit((data) => createContactMutation.mutate(data))}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={contactForm.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select contact type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="call">Phone Call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="text">Text/SMS</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={contactForm.control}
                name="contactDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Date</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={contactForm.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input placeholder="Brief summary of the interaction" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={contactForm.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Detailed notes about the interaction..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={contactForm.control}
                name="outcome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Outcome</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select outcome" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="successful">Successful</SelectItem>
                        <SelectItem value="needs_follow_up">Needs Follow-up</SelectItem>
                        <SelectItem value="no_response">No Response</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="escalated">Escalated</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={contactForm.control}
                name="nextFollowUp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Next Follow-up</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsContactDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createContactMutation.isPending}>
                {createContactMutation.isPending ? "Adding..." : "Add Contact"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );

  return (
    <div
      className="bg-white dark:bg-gray-900 rounded-[12px] border border-[#E5E7EB] dark:border-gray-700 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 mt-[2px] text-[#475569]" />
          <div>
            <h3
              className="font-bold text-[#0F172A] dark:text-gray-100"
              style={{ fontSize: "14px" }}
            >
              Contact History
            </h3>
            <p
              className="text-[#475569] dark:text-gray-400 mt-0.5"
              style={{ fontSize: "12px" }}
            >
              {headerSubtitle}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {contactInteractions.length > 0 && (
            <Select
              value={typeFilter}
              onValueChange={(v) => {
                setTypeFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9 w-[140px] text-sm">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="call">Phone</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="other">Note</SelectItem>
              </SelectContent>
            </Select>
          )}
          {logContactTrigger()}
        </div>
      </div>

      {/* Body */}
      {contactInteractionsLoading ? (
        <div className="flex items-center justify-center py-12 border-t border-[#EEF0F4]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : contactInteractions.length === 0 ? (
        // Empty state
        <div className="border-t border-[#EEF0F4] flex flex-col items-center justify-center py-12 px-5 text-center">
          <h4
            className="font-semibold text-[#0F172A] dark:text-gray-100"
            style={{ fontSize: "14px" }}
          >
            No contact logged yet
          </h4>
          <p
            className="mt-1 text-[#475569] dark:text-gray-400"
            style={{ fontSize: "12.5px" }}
          >
            Use Log contact above to record the first touchpoint with {clientName}.
          </p>
        </div>
      ) : total === 0 ? (
        <div
          className="border-t border-[#EEF0F4] py-8 px-5 text-center text-[#475569]"
          style={{ fontSize: "12.5px" }}
        >
          No {TYPE_PILL[typeFilter]?.label.toLowerCase() ?? typeFilter} touchpoints
          logged for this client.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr style={{ backgroundColor: "#F8FAFC" }}>
                {SORT_COLUMNS.map(({ key, label }) => (
                  <th
                    key={key}
                    className="text-left font-bold uppercase text-[#475569] cursor-pointer select-none hover:text-[#0F172A]"
                    style={{
                      fontSize: "10.5px",
                      letterSpacing: "0.5px",
                      padding: "10px 20px",
                    }}
                    onClick={() => toggleSort(key)}
                    title={`Sort by ${label}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {label}
                      {sortKey === key &&
                        (sortDir === "asc" ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        ))}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleInteractions.map((interaction, idx) => {
                const meta = TYPE_PILL[interaction.type as string] ?? TYPE_PILL.other;
                const overdue = isOverdue(interaction.nextFollowUp);
                const rowBorder =
                  idx === 0 ? undefined : "1px solid #EEF0F4";
                return (
                  <tr key={interaction.id}>
                    {/* Date */}
                    <td
                      className="align-top text-[#475569] whitespace-nowrap"
                      style={{
                        fontFamily: "ui-monospace, monospace",
                        fontSize: "12.5px",
                        padding: "12px 20px",
                        borderTop: rowBorder,
                      }}
                    >
                      {formatHandoffDate(interaction.contactDate)}
                    </td>
                    {/* Type pill */}
                    <td
                      className="align-top"
                      style={{ padding: "12px 20px", borderTop: rowBorder }}
                    >
                      <span
                        className="inline-flex items-center font-semibold rounded-full"
                        style={{
                          backgroundColor: meta.bg,
                          color: meta.fg,
                          fontSize: "11.5px",
                          padding: "2px 8px",
                          gap: "6px",
                        }}
                      >
                        <meta.Icon className="h-3.5 w-3.5" />
                        {meta.label}
                      </span>
                    </td>
                    {/* Subject */}
                    <td
                      className="align-top text-[#0F172A] dark:text-gray-100 font-medium"
                      style={{ padding: "12px 20px", borderTop: rowBorder }}
                    >
                      {interaction.subject}
                      {interaction.notes && (
                        <div
                          className="text-[#475569] mt-1 font-normal"
                          style={{ fontSize: "12px" }}
                        >
                          {interaction.notes}
                        </div>
                      )}
                    </td>
                    {/* Outcome */}
                    <td
                      className="align-top text-[#475569]"
                      style={{
                        padding: "12px 20px",
                        borderTop: rowBorder,
                        maxWidth: "320px",
                      }}
                    >
                      {OUTCOME_LABELS[interaction.outcome as any] || (
                        <span className="italic text-[#94A3B8]">—</span>
                      )}
                    </td>
                    {/* Next follow-up */}
                    <td
                      className="align-top"
                      style={{ padding: "12px 20px", borderTop: rowBorder }}
                    >
                      {interaction.nextFollowUp ? (
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="font-semibold"
                              style={{
                                fontFamily: "ui-monospace, monospace",
                                fontSize: "12px",
                                color: overdue ? "#B91C1C" : "#0F172A",
                              }}
                            >
                              {formatHandoffDate(interaction.nextFollowUp)}
                            </span>
                            {overdue && (
                              <span
                                className="font-bold uppercase rounded-full"
                                style={{
                                  backgroundColor: "#FEF2F2",
                                  color: "#B91C1C",
                                  fontSize: "10px",
                                  padding: "0 6px",
                                  letterSpacing: "0.2px",
                                }}
                              >
                                Overdue
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            <button
                              type="button"
                              onClick={() => addTaskMutation.mutate(interaction)}
                              disabled={addTaskMutation.isPending}
                              title="Create a follow-up task due on this date"
                              className="inline-flex items-center gap-1 rounded border border-[#E5E7EB] px-1.5 py-0.5 text-[#475569] hover:bg-[#F8FAFC] hover:text-[#1D3FBD] disabled:opacity-50"
                              style={{ fontSize: "11px" }}
                            >
                              <ClipboardList className="h-3 w-3" />
                              Add task
                            </button>
                            <button
                              type="button"
                              onClick={() => addToCalendarMutation.mutate(interaction)}
                              disabled={addToCalendarMutation.isPending}
                              title="Schedule a calendar appointment on this date"
                              className="inline-flex items-center gap-1 rounded border border-[#E5E7EB] px-1.5 py-0.5 text-[#475569] hover:bg-[#F8FAFC] hover:text-[#1D3FBD] disabled:opacity-50"
                              style={{ fontSize: "11px" }}
                            >
                              <CalendarPlus className="h-3 w-3" />
                              Add to calendar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span className="italic text-[#94A3B8]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      {contactInteractions.length > 0 && (
        <div
          className="flex items-center justify-between"
          style={{
            padding: "10px 20px",
            borderTop: "1px solid #E5E7EB",
            fontSize: "12px",
            color: "#475569",
          }}
        >
          <span>
            {total === 0
              ? "No touchpoints match this filter"
              : `Showing ${rangeStart}–${rangeEnd} of ${total}${typeFilter !== "all" ? " (filtered)" : ""}`}
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="inline-flex items-center gap-0.5 font-semibold hover:underline disabled:opacity-40 disabled:no-underline"
                style={{ color: "#1D3FBD", fontSize: "12px" }}
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="inline-flex items-center gap-0.5 font-semibold hover:underline disabled:opacity-40 disabled:no-underline"
                style={{ color: "#1D3FBD", fontSize: "12px" }}
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactHistoryTab;
